import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import {
  ProductionKeyValuePairGet,
  ProductionKeyValuePairPut,
} from "../../../../models/ProductionKeyValuePair"

const validationSchema = z.object({
  key: z.string().min(1, "Key is required"),
  value: z.string().min(1, "Value is required"),
})

const returnURL = `/production#keyValuePairs`

export function KeyValuePairDetails(props: {
  readonly productionKeyValuePair: ProductionKeyValuePairGet
  readonly setProductionKeyValuePair: (
    updatedKeyValuePair: ProductionKeyValuePairGet
  ) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { productionKeyValuePair, setProductionKeyValuePair } = props

  const formMethods = useForm<ProductionKeyValuePairPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      key: productionKeyValuePair.key,
      value: productionKeyValuePair.value,
    })
  }, [productionKeyValuePair, reset])

  const onSubmit = useCallback(
    async (values: ProductionKeyValuePairPut) => {
      try {
        const res = await digitalManagerApi.put<ProductionKeyValuePairGet>(
          `/production/keyValuePairs/${productionKeyValuePair.id}`,
          values
        )
        toast.success("Production key-value updated")
        setProductionKeyValuePair(res.data)
      } catch {
        toast.error("Cannot update production key-value")
        throw new Error("Cannot update production key-value")
      }
    },
    [productionKeyValuePair.id, setProductionKeyValuePair]
  )

  const canWrite = useMemo(
    () =>
      user
        ? hasUserAccessToFeature(
            user.authorizations,
            DimaFeatureShortNameEnum.PR,
            AccessLevel.Write
          )
        : false,
    [user]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<ProductionKeyValuePairPut, "key" | "value">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldKey = useCallback(
    ({ field }: { field: ControllerRenderProps<ProductionKeyValuePairPut, "key"> }) => (
      <MUITextField
        id="key"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.key?.message === "string"
            ? { key: { message: errors.key.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.key, handleTextFieldChange]
  )

  const renderTextFieldValue = useCallback(
    ({ field }: { field: ControllerRenderProps<ProductionKeyValuePairPut, "value"> }) => (
      <MUITextField
        id="value"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.value?.message === "string"
            ? { value: { message: errors.value.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.value, handleTextFieldChange]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<ProductionKeyValuePairPut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Key",
        displayItem: <div style={{ width: "100%" }}>{productionKeyValuePair.key}</div>,
        editItem: <Controller name="key" control={control} render={renderTextFieldKey} />,
      },
      {
        id: 1,
        label: "Value",
        displayItem: <div style={{ width: "100%" }}>{productionKeyValuePair.value}</div>,
        editItem: (
          <Controller name="value" control={control} render={renderTextFieldValue} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
